import { AlertTriangle } from "lucide-react";
import {
  messageFullDate,
  messageTime,
  type ConversationMessageItem,
} from "@/lib/conversations";
import ConversationMessageContent from "./ConversationMessageContent";

/** Burbuja de mensaje (bot a la derecha en verde, staff a la derecha en
 *  primary, cliente a la izquierda), compartida entre el inbox del inquilino
 *  y el inbox de soporte de plataforma. El nombre del cliente y el del staff
 *  se resuelven FUERA del componente (cada inbox identifica al "cliente" de
 *  forma distinta: ficha de `Customer` en uno, empresa emparejada en el
 *  otro) — aquí solo se pintan las etiquetas ya resueltas. */
export default function ConversationMessageBubble({
  message,
  customerLabel,
  staffLabel,
  mediaBasePath,
}: {
  message: ConversationMessageItem;
  customerLabel: string;
  staffLabel: (message: ConversationMessageItem) => string;
  /** Base del endpoint de adjuntos del inbox que pinta ("/whatsapp-conversations/<id>/media"
   *  o "/admin/platform-conversations/<id>/media"): cada inbox tiene el suyo.
   *  Sin él, un audio, foto o vídeo se queda en el texto guardado. */
  mediaBasePath?: string;
}) {
  const isBot = message.senderType === "bot";
  const isStaff = message.senderType === "staff";
  const isRight = isBot || isStaff;
  // Reacción (emoji sobre un mensaje): se pinta como una nota discreta, no
  // como una burbuja más, igual que hace WhatsApp. El cuerpo llega del backend
  // con el formato «Reaccionó con 👍 a tu mensaje: «…»» (ver describeReaction).
  const isReaction = message.messageType === "reaction";
  const label = isBot
    ? "Asistente IA"
    : isStaff
      ? staffLabel(message)
      : customerLabel;

  if (isReaction) {
    const emoji = message.body.match(/\p{Extended_Pictographic}/u)?.[0] ?? "💬";
    const quote = message.body.match(/«([^»]*)»/)?.[1] ?? "";
    return (
      <div className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
        <div className="max-w-[85%] md:max-w-[70%] min-w-0 flex items-center gap-2 text-xs text-gray-500">
          <span className="text-lg leading-none">{emoji}</span>
          <span className="truncate">
            {label} reaccionó{quote && <span className="italic"> a «{quote}»</span>}
          </span>
          <span className="text-gray-400 flex-shrink-0" title={messageFullDate(message.createdAt)}>
            {messageTime(message.createdAt)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%] md:max-w-[70%] min-w-0">
        <div
          className={`flex items-center gap-1.5 mb-1 ${isRight ? "flex-row-reverse" : ""}`}
        >
          <span className="text-xs font-medium text-gray-500 truncate">
            {label}
          </span>
          {isBot && (
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold flex-shrink-0">
              IA
            </span>
          )}
        </div>
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isBot
              ? "bg-emerald-50 border border-emerald-200 text-gray-800 rounded-tr-md"
              : isStaff
                ? "bg-primary-600 text-white rounded-tr-md"
                : "bg-white border border-gray-200 text-gray-800 rounded-tl-md"
          }`}
        >
          <ConversationMessageContent
            message={message}
            mediaUrl={mediaBasePath ? `${mediaBasePath}/${message.id}` : undefined}
          />
          {/* Hora dentro de la burbuja, abajo a la derecha, como en WhatsApp.
              El día lo da el separador de fecha que pinta cada inbox. */}
          <span
            title={messageFullDate(message.createdAt)}
            className={`block text-right text-[11px] leading-none mt-1 ${
              isStaff ? "text-white/75" : "text-gray-400"
            }`}
          >
            {messageTime(message.createdAt)}
          </span>
        </div>
        {message.notDelivered && (
          <div className={`flex items-center mt-1 ${isRight ? "justify-end" : ""}`}>
            <span className="flex items-center gap-0.5 text-xs text-red-600 font-medium">
              <AlertTriangle className="w-3 h-3" /> No entregado
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
